import appConfigs from "@configs/app";
import { notDefined, isEmpty } from "@utils/var.check";
import { cookieManager } from "@utils/cookie.manager";
import googleAuthApi from "@/apis/user.google.credential.api";
import { userGoogleCredential } from "@plugins/google/user.google.credential";
import { GoogleAuthEventBus } from "@plugins/google/google.auth.event.bus";
import { CURRENT_USER_GOOGLE_CREDENTIALS_COOKIE } from "@plugins/google/google.constant";
import { GoogleClientCredential } from "@plugins/google/types.ts";

class GoogleAuth {
  userLinkedEmail = ""; // email that user linked to access google
  requireSignIn = true;
  codeClient;
  currentUser;
  googleClientCredential: GoogleClientCredential | null = null;

  constructor() {
    // empty
  }

  async fetchGoogleClientCredential() {
    if (this.googleClientCredential == null) {
      const googleClientCredentialResponse = await googleAuthApi.getGoogleClientCredential();
      if (googleClientCredentialResponse.code == "ok") {
        this.googleClientCredential = googleClientCredentialResponse.data;
      }
    }
  }

  handleRequestCode() {
    this.codeClient.requestCode();
  }

  setUserLinkedEmail() {
    this.userLinkedEmail = userGoogleCredential.getUserLinkedAccountEmail();
  }

  revokeAccess() {
    this.requireSignIn = true;
    window.google.accounts.oauth2.revoke(this.currentUser.accessToken, () => {
      this.currentUser = null;
      this.userLinkedEmail = "";
      cookieManager.removeCookie(CURRENT_USER_GOOGLE_CREDENTIALS_COOKIE);
      console.log("Revoked current user access token.");
    });
  }

  async checkUserCodeExpire() {
    userGoogleCredential.checkAccessTokenExpiredState();
    if (userGoogleCredential.isAccessTokenExpired) {
      this.signIn().then(() => {
        return Promise.resolve();
      });
    }
  }

  signIn() {
    return new Promise(() => {
      userGoogleCredential.fetchLinkedAccountCredential().then(() => {
        this.setUserLinkedEmail();
        this.handleRequestCode();
      });
    });
  }

  async checkAuthInstanceInitState() {
    const savedUserCredentials = cookieManager.getCookie(CURRENT_USER_GOOGLE_CREDENTIALS_COOKIE);

    if (savedUserCredentials) {
      const decryptedUserCredentials = window.atob(savedUserCredentials);
      this.currentUser = JSON.parse(decryptedUserCredentials);
    }
    await this.fetchGoogleClientCredential();
    if (notDefined(window.google)) {
      await this.isEmailSync();
      await this.loadScript();
    } else {
      await this.init();
    }
  }

  async isEmailSync() {
    await userGoogleCredential.fetchLinkedAccountCredential();
    this.setUserLinkedEmail();
    try {
      const needAuthorize =
        !userGoogleCredential.hasAccessToken() ||
        (!isEmpty(this.userLinkedEmail) &&
          !isEmpty(this.currentUser) &&
          this.userLinkedEmail !== this.currentUser.email);
      if (needAuthorize) {
        console.log("Email not synced, required to sign in again.");
        cookieManager.removeCookie(CURRENT_USER_GOOGLE_CREDENTIALS_COOKIE);
        this.requireSignIn = true;
      } else {
        console.log("Email is synced.");
        this.requireSignIn = false;
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  loadScript() {
    return new Promise((resolve) => {
      const script = appConfigs.googleAuthLibraryScript;
      const scriptEl = document.createElement("script");
      for (const key in script) {
        if (script[key]) {
          scriptEl.setAttribute(key, script[key]);
        }
      }
      document.head.appendChild(scriptEl);
      scriptEl.onload = () => {
        this.init().then(() => {
          resolve();
        });
      };
    });
  }

  hasGrantedDriveScope(codeResponse) {
    return window.google.accounts.oauth2.hasGrantedAnyScope(
      codeResponse,
      "https://www.googleapis.com/auth/drive",
    );
  }

  createCurrentGoogleUserCredentialsCookie(userCredentialsData) {
    const cookieDataJsonString = JSON.stringify({
      email: userCredentialsData.email,
      accessToken: userCredentialsData.accessToken,
    });
    const cookieData = window.btoa(cookieDataJsonString);

    cookieManager.createCookie(CURRENT_USER_GOOGLE_CREDENTIALS_COOKIE, cookieData, null);
  }

  init() {
    return new Promise((resolve) => {
      this.codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: this.googleClientCredential.clientId,
        scope: appConfigs.googleAuthClientScope,
        hint: userGoogleCredential.hasAccessToken() ? this.userLinkedEmail : "",
        ux_mode: "popup",
        callback: (response) => {
          if (!this.hasGrantedDriveScope(response)) {
            this.requireSignIn = true;
            GoogleAuthEventBus.$emit("handleCallback");
            return;
          }

          googleAuthApi.linkAccount(response.code).then((userCredentials) => {
            this.createCurrentGoogleUserCredentialsCookie(userCredentials.data);
            userGoogleCredential.setCredential(userCredentials.data);
            this.currentUser = userCredentials.data;

            if (!isEmpty(this.currentUser.email)) {
              this.isEmailSync().then(() => {
                GoogleAuthEventBus.$emit("handleCallback");
              });
            } else {
              this.requireSignIn = true;
              console.log("Required to sign in.");
            }
          });
        },
        error_callback: () => {
          GoogleAuthEventBus.$emit("handleErrorCallback");
        },
      });
      console.log("codeClientInstanceLoaded");
      resolve();
    });
  }
}

const googleAuth = new GoogleAuth();

export { googleAuth };
